// Icon set — a small, dependency-free SVG library tailored to NewsWave.
// Stroke / size props match the mobile design system so the visual weight
// matches across surfaces. All icons are 24×24 by default and scale via
// the `size` prop. Colour is `currentColor` so they inherit text colour.

import type { SVGProps } from 'react';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'size' | 'stroke'> {
  readonly size?: number;
  readonly stroke?: number;
  readonly filled?: boolean;
}

const base = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
});

export const HomeIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

export const BookIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 01-3-3V4z" />
    <path d="M4 4v13a3 3 0 003 3" />
    <path d="M8 8h7" />
    <path d="M8 12h7" />
  </svg>
);

export const UserIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
  </svg>
);

export const GlobeIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13 13 0 010 18" />
    <path d="M12 3a13 13 0 000 18" />
  </svg>
);

export const TranslateIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M3 6h11" />
    <path d="M8 4v2" />
    <path d="M5 11s2 4 5 4 5-4 5-4" />
    <path d="M11 6c0 4-2 8-7 9" />
    <path d="M14 21l4-9 4 9" />
    <path d="M16 17h4" />
  </svg>
);

export const BellIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9z" />
    <path d="M10 21a2 2 0 004 0" />
  </svg>
);

export const ShieldIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
  </svg>
);

export const TargetIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

export const PlaneIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M21 16l-7-4V5a2 2 0 00-4 0v7l-7 4v2l7-2v4l-2 2v1l4-1 4 1v-1l-2-2v-4l7 2z" />
  </svg>
);

export const PinIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export const BookmarkIcon = ({ size = 24, stroke = 2, filled = false, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} fill={filled ? 'currentColor' : 'none'} {...rest}>
    <path d="M6 4h12v17l-6-4-6 4V4z" />
  </svg>
);

export const MoonIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M21 13a8 8 0 11-10-10 6 6 0 0010 10z" />
  </svg>
);

export const SunIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v3" />
    <path d="M12 19v3" />
    <path d="M2 12h3" />
    <path d="M19 12h3" />
    <path d="M4.9 4.9l2.1 2.1" />
    <path d="M17 17l2.1 2.1" />
    <path d="M4.9 19.1L7 17" />
    <path d="M17 7l2.1-2.1" />
  </svg>
);

export const CheckIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M5 12l5 5L20 7" />
  </svg>
);

export const ChevronIcon = ({
  size = 24,
  stroke = 2,
  dir = 'right',
  ...rest
}: IconProps & { readonly dir?: 'up' | 'down' | 'left' | 'right' }) => {
  const rot = { up: -90, right: 0, down: 90, left: 180 }[dir];
  return (
    <svg {...base(size)} strokeWidth={stroke} style={{ transform: `rotate(${rot}deg)`, transition: 'transform 160ms ease' }} {...rest}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
};

export const XIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </svg>
);

export const SearchIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

export const SparkleIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M12 4v4" />
    <path d="M12 16v4" />
    <path d="M4 12h4" />
    <path d="M16 12h4" />
    <path d="M6 6l2.5 2.5" />
    <path d="M15.5 15.5L18 18" />
    <path d="M6 18l2.5-2.5" />
    <path d="M15.5 8.5L18 6" />
  </svg>
);

export const AlertIcon = ({ size = 24, stroke = 2, ...rest }: IconProps) => (
  <svg {...base(size)} strokeWidth={stroke} {...rest}>
    <path d="M12 3l10 18H2L12 3z" />
    <path d="M12 10v5" />
    <circle cx="12" cy="18" r="0.5" fill="currentColor" />
  </svg>
);
